<script lang="ts" setup>
import { computed, Ref, ref, watch } from "vue";
import { MateTraitAnimation } from "@/types/entity";
import Gallery, {
  GalleryInstance,
  UniqueMedia,
} from "@/components/mobile/media/Gallery.vue";
import LightArrowIcon from "@/components/icon/LightArrowIcon.vue";
import { useMate } from "@/stores/mate";
import { useTraitAnimation } from "@/stores/traitAnimation";
import { useUI } from "@/stores/ui";
// 1. 常量
// 2. Props & Emit & Expose
export interface AnimationGalleryInstance {
  clear: () => void;
  selectedAnimationList: Ref<MateTraitAnimation[]>;
}

const props = withDefaults(
  defineProps<{
    selectable?: boolean;
    enableUpload?: boolean;
    maxSelectCount?: number;
    enableMateChange?: boolean;
    traitId?: number;
  }>(),
  {
    selectable: true,
    enableUpload: false,
    maxSelectCount: 1,
    enableMateChange: true,
  }
);

const emit = defineEmits<{
  (e: "back"): void;
  (e: "onConfirm", animations: MateTraitAnimation[]): void;
}>();
// 3. 内部状态
const traitAnimation = useTraitAnimation();
const mate = useMate();
const animationList = ref<MateTraitAnimation[]>([]);
const mediaList = computed<UniqueMedia[]>(() =>
  animationList.value.map(
    (item) =>
      ({
        ...item.portraitzz[0].portrait.media,
        uniqueId: item.id,
      } as UniqueMedia)
  )
);

const galleryRef = ref<GalleryInstance | null>(null);
const currentMateId = ref(mate.currentMate!.id);
const currentMate = computed(() => mate.getMyMateById(currentMateId.value));
const loading = ref(false);
const selectedAnimationList = computed(() =>
  galleryRef.value!.selectedMediaList.map(
    (item) => traitAnimation.animationMap[item.uniqueId!] as MateTraitAnimation
  )
);

defineExpose<AnimationGalleryInstance>({
  clear: (clearFilter?: boolean) => {
    galleryRef.value!.clear(clearFilter);
  }, // method: 刷新gallery列表
  selectedAnimationList,
});

// Event Handler
const uiStore = useUI();
const onMateChange = () => {
  if (props.enableMateChange) {
    // showMateSelect.value = true;
    uiStore.popMateSelector({
      switchMate: false,
      callback: (mate) => {
        currentMateId.value = mate.id;
        galleryRef.value?.clear();
        uiStore.showMateSelector = false;
      },
    });
  }
};

const fetchList = async () => {
  animationList.value = await traitAnimation.getOrFetchAnimationByMateId(
    currentMateId.value,
    props.traitId
  );
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr]">
    <div class="navbar bg-base-100 h-top-nav border-b">
      <!-- 左 -->
      <div class="navbar-start h-full">
        <button class="btn btn-ghost" @click="emit('back')">
          <light-arrow-icon />
        </button>
      </div>
      <div class="navbar-center cursor-pointer" @click="onMateChange">
        <div>
          {{ currentMate?.name }}
          <i v-if="enableMateChange" class="iconfont icon-down1 text-sm"></i>
        </div>
      </div>
      <div class="navbar-end flex gap-1">
        <button
          :disabled="galleryRef?.selectedMediaList.length === 0"
          class="btn btn-primary btn-xs md:btn-sm rounded"
          @click="emit('onConfirm', selectedAnimationList)"
        >
          {{ $t("Buttons.confirm") }}
        </button>
      </div>
    </div>
    <div class="h-full overflow-hidden relative">
      <gallery
        ref="galleryRef"
        :filter="1"
        :media-list="mediaList"
        :on-scroll-load="fetchList"
        use-unique-id
        v-bind="props"
      />
      <div
        v-show="loading"
        class="top-0 left-0 right-0 bottom-0 flex w-full h-full items-center justify-center absolute bg-base-100"
      >
        <span class="loading"></span>
      </div>
    </div>
    <!-- <MateSelect v-model:mate="currentMateId" v-model:show="showMateSelect" /> -->
  </div>
</template>
